import React from 'react';
import { useTiptapEditor } from 'ra-input-rich-text';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, ToggleButton } from '@mui/material';
import InsertLink from '@mui/icons-material/InsertLink';
import { LINK_MAX_LENGTH } from '../common';
import { errorOutlinedButtonSx, filledButtonSx, outlinedButtonSx } from '../buttons';

// Lo que admite la extensión link (`https?:` o `mailto:`), con algo detrás del esquema.
const VALID_HREF = /^(https?:\/\/\S+|mailto:\S+)$/i;

/** El error de la URL escrita, o null si se puede guardar (mismas reglas que la extensión link). */
export const linkUrlError = (url) => {
    if (!url) return null;
    if (!VALID_HREF.test(url)) return 'Escribe una URL que empiece por https://, http:// o mailto:.';
    if (url.length > LINK_MAX_LENGTH) return `Un enlace no puede superar ${LINK_MAX_LENGTH} caracteres.`;
    return null;
};

/**
 * Botón de enlace de la barra del texto. Sustituye a `LinkButtons` de ra-input-rich-text, que pide
 * la URL con el `window.prompt` del navegador: aquí es un diálogo del panel, que trae la URL del
 * enlace donde está el cursor y deja quitarlo.
 */
const LinkButton = ({ size }) => {
    const editor = useTiptapEditor();
    const [, forceRender] = React.useReducer((n) => n + 1, 0);
    const [open, setOpen] = React.useState(false);
    const [url, setUrl] = React.useState('');

    React.useEffect(() => {
        if (!editor) return undefined;
        editor.on('transaction', forceRender);
        return () => editor.off('transaction', forceRender);
    }, [editor]);

    const inLink = !!editor?.isActive('link');
    const canLink = !!editor?.isEditable && (inLink || !editor.state.selection.empty);
    const trimmed = url.trim();
    const error = linkUrlError(trimmed);

    const openDialog = () => {
        setUrl(editor.getAttributes('link').href || '');
        setOpen(true);
    };
    const close = () => setOpen(false);
    const save = () => {
        if (!trimmed || error) return;
        editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
        close();
    };
    const remove = () => {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        close();
    };

    return (
        <>
            <ToggleButton
                value="link"
                size={size}
                aria-label="Enlace"
                title="Enlace"
                selected={inLink}
                disabled={!canLink}
                onChange={openDialog}
            >
                <InsertLink fontSize="inherit" />
            </ToggleButton>
            <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="news-link-dialog-title">
                <DialogTitle id="news-link-dialog-title">{inLink ? 'Editar enlace' : 'Añadir enlace'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="URL"
                        placeholder="https://"
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                save();
                            }
                        }}
                        error={!!error}
                        helperText={error || 'El texto seleccionado llevará a esta dirección.'}
                        inputProps={{ 'data-testid': 'news-link-url' }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    {inLink && (
                        <Button onClick={remove} sx={[errorOutlinedButtonSx, { mr: 'auto' }]}>
                            Quitar enlace
                        </Button>
                    )}
                    <Button onClick={close} color="inherit" sx={outlinedButtonSx}>
                        Cancelar
                    </Button>
                    <Button onClick={save} variant="contained" disabled={!trimmed || !!error} sx={filledButtonSx}>
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LinkButton;
